import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_KEY = "controlenext-theme";
// Só existe depois que a pessoa troca o tema no botão. Sem ela o app abre no escuro - inclusive
// pra quem tem "light" gravado só porque o aparelho estava no claro na primeira visita (antes o
// tema inicial seguia o sistema e ficava gravado como se fosse escolha).
const CHOSEN_KEY = "controlenext-theme-chosen";

// O index.html repete essa regra num script antes do React montar, pra abertura não piscar no claro.
function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (localStorage.getItem(CHOSEN_KEY) === "1" && (stored === "light" || stored === "dark")) return stored;
  } catch {
    // sem localStorage: fica no padrão
  }
  return "dark";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // idem
    }
  }, [theme]);

  const toggleTheme = () => {
    try {
      localStorage.setItem(CHOSEN_KEY, "1");
    } catch {
      // idem
    }
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  };

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme deve ser usado dentro de ThemeProvider");
  return ctx;
}
